import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentPlansService } from '../../../services/payment-plans.service';
import {
  PaymentPlanVersionReason,
  PaymentPlanVersionReasonLabels
} from '../../../enums/PaymentPlanVersionReason';
import { AuthService } from '../../../services/auth.service';
import { AuthorizationService } from '../../../services/authorization.service';
import {
  PaymentPlanAdminService,
  MoveBatchToVersionRequest,
  ConsolidateActiveVersionRequest,
  SetActiveVersionRequest,
  PromoteGroupToVersionRequest
} from '../../../services/payment-plan-admin.service';
import {
  CorporatePaymentPlanAdminService,
  PromoteGroupToCorporateVersionRequest
} from '../../../services/corporate-payment-plan-admin.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { finalize } from 'rxjs/operators';

interface HistoryReceipt {
  id: number;
  referenceNumber?: string;
  transactionDate: string;
  createdAt?: string;
  grossAmount: number;
  netAmount: number;
  totalFees: number;
  paymentMethodName?: string;
  bankName?: string;
  transactionId?: string;
  idPaymentLink?: string;
  isActive: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isBankReconciled: boolean;
  approvalStatus?: string;
}

interface HistoryPaymentLink {
  id: number;
  wompiLinkId?: string;
  paymentUrl?: string;
  generatedDate: string;
  expirationDate: string;
  isExpired: boolean;
  webhookProcessed: boolean;
  webhookDate?: string;
  emailSent: boolean;
  amount: number;
  originalAmount?: number;
  currencyCode?: string;
  isActive: boolean;
}

interface HistoryWebhook {
  id: number;
  transactionId: string;
  requestDate: string;
  processedDate?: string;
  eventName?: string;
  transactionStatus?: string;
  status?: string;
  amountInCents: number;
  currency?: string;
  paymentMethodType?: string;
  customerEmail?: string;
  failureStep?: string;
  errorMessage?: string;
  retryCount: number;
}

interface HistoryCuota {
  id: number;
  installmentNumber: number;
  dueDate: string;
  amountToPay: number;
  amountPaid: number;
  paymentStatus: string | number;
  payerType: string | number;
  isPunished: boolean;
  isActive: boolean;
  // 🆕 Versionado por grupo
  isLocked?: boolean;
  lineageId?: number;
  movementType: string | number;
  movementDate?: string | null;
  participantId?: number | null;
  participantName?: string | null;
  migratedFromPaymentPlanId?: number | null;
  incomeReceipts: HistoryReceipt[];
  paymentLink?: HistoryPaymentLink | null;
  wompiWebhooks: HistoryWebhook[];
  _expanded?: boolean;
  /**
   * 🆕 Posición de selección en modo multi-mover (1, 2, 3...). Null = no seleccionada.
   * UI-only, no se persiste ni se envía al backend.
   */
  _selectionOrder?: number | null;
}

interface HistoryVersion {
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
  installmentCount: number;
  totalAmountToPay: number;
  totalAmountPaid: number;
  cuotas: HistoryCuota[];
  // 🆕 Metadatos del versionado por grupo
  paymentPlanVersionId?: number | null;
  reason?: number | null;
  reasonComment?: string | null;
  performedBy?: string | null;
  supersededByVersionId?: number | null;
  previousVersionId?: number | null;
  participantId?: number | null;
  participantName?: string | null;
  scope?: string | null;
  _expanded?: boolean;
}

interface HistoryResponse {
  enrollmentId: number;
  enrollmentType: 'individual' | 'corporate';
  versions: HistoryVersion[];
}

@Component({
  selector: 'app-payment-plan-history',
  templateUrl: './payment-plan-history.component.html'
})
export class PaymentPlanHistoryComponent implements OnInit {
  enrollmentId = 0;
  enrollmentType: 'individual' | 'corporate' = 'individual';
  participantIdFilter: number | null = null;
  participantNameFilter: string | null = null;
  loading = false;
  error: string | null = null;
  history: HistoryResponse | null = null;
  filteredVersions: HistoryVersion[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paymentPlansService: PaymentPlansService,
    private authService: AuthService,
    private authorizationService: AuthorizationService,
    private adminService: PaymentPlanAdminService,
    private corpAdminService: CorporatePaymentPlanAdminService,
    private toast: ToastService,
    private loadingService: LoadingService
  ) {}

  // ============================================================
  // 🔐 Mover cuota a otra versión — gated por permiso
  // ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS (espejo del backend).
  // ============================================================
  get isSuperAdmin(): boolean {
    return this.authorizationService.hasPermission('ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS');
  }

  /** Estado del modal "Mover a versión". */
  moveModal: {
    open: boolean;
    cuota: HistoryCuota | null;
    sourceVersion: HistoryVersion | null;
    targetVersionId: number | null;
    comment: string;
    /** 🆕 Si true, el backend no modifica Enrollment.TotalAmount aunque la suma cambie. */
    preserveTotalAmount: boolean;
  } = { open: false, cuota: null, sourceVersion: null, targetVersionId: null, comment: '', preserveTotalAmount: false };

  /** 🆕 Estado del modal de confirmación secundario (cuando backend pide confirm). */
  confirmDialog: {
    open: boolean;
    title: string;
    message: string;
    currentValue: number | null;
    proposedValue: number | null;
    confirmationKey: string | null;
  } = { open: false, title: '', message: '', currentValue: null, proposedValue: null, confirmationKey: null };

  // ============================================================
  // 🆕 Activar cuotas archivadas (single + batch)
  // ============================================================
  /** Ids de cuotas archivadas seleccionadas para activar en batch. */
  selectedActivateIds = new Set<number>();
  /** Scope ancla derivado de la primera selección. Bloquea mezclar versiones/scopes. */
  activateAnchor: {
    versionId: number | null | undefined;
    participantId: number | null | undefined;
    payerType: string | number | null | undefined;
  } | null = null;

  /** Estado del modal "Activar cuota(s)". */
  activateModal: {
    open: boolean;
    mode: 'single' | 'batch';
    cuota: HistoryCuota | null;          // sólo en mode='single'
    versionRef: HistoryVersion | null;    // versión donde está la cuota / batch
    cuotaIds: number[];                   // ids efectivos (1 o N)
    comment: string;
  } = { open: false, mode: 'single', cuota: null, versionRef: null, cuotaIds: [], comment: '' };

  /** ¿Esta cuota archivada se puede marcar/desmarcar dado el ancla actual? */
  canSelectForActivate(c: HistoryCuota, v: HistoryVersion): boolean {
    if (c.isActive) return false;
    if (!this.activateAnchor) return true;
    if (this.activateAnchor.versionId !== v.paymentPlanVersionId) return false;
    if (this.enrollmentType === 'corporate') {
      if (this.activateAnchor.participantId !== c.participantId) return false;
      if (this.activateAnchor.payerType !== c.payerType) return false;
    }
    return true;
  }

  /** Razón por la cual el checkbox aparece deshabilitado (tooltip). */
  selectActivateBlockedReason(c: HistoryCuota, v: HistoryVersion): string {
    if (c.isActive) return '';
    if (!this.activateAnchor) return '';
    if (this.activateAnchor.versionId !== v.paymentPlanVersionId) {
      return 'Para activar en batch, todas las cuotas deben pertenecer a la misma versión que la primera seleccionada.';
    }
    if (this.enrollmentType === 'corporate') {
      if (this.activateAnchor.participantId !== c.participantId
          || this.activateAnchor.payerType !== c.payerType) {
        return 'En corporativo todas las cuotas deben compartir scope (mismo participante y payerType).';
      }
    }
    return '';
  }

  toggleActivateSelection(c: HistoryCuota, v: HistoryVersion, ev?: Event): void {
    if (ev) ev.stopPropagation();
    if (c.isActive) return;
    if (this.selectedActivateIds.has(c.id)) {
      this.selectedActivateIds.delete(c.id);
      if (this.selectedActivateIds.size === 0) this.activateAnchor = null;
      return;
    }
    if (!this.canSelectForActivate(c, v)) return;
    if (!this.activateAnchor) {
      this.activateAnchor = {
        versionId: v.paymentPlanVersionId,
        participantId: c.participantId,
        payerType: c.payerType
      };
    }
    this.selectedActivateIds.add(c.id);
  }

  clearActivateSelection(): void {
    this.selectedActivateIds.clear();
    this.activateAnchor = null;
  }

  /** Abre el modal en modo single para una cuota archivada (botón "Activar" por fila). */
  openActivateSingle(c: HistoryCuota, v: HistoryVersion): void {
    this.activateModal = {
      open: true,
      mode: 'single',
      cuota: c,
      versionRef: v,
      cuotaIds: [c.id],
      comment: ''
    };
  }

  /** Abre el modal en modo batch usando la selección actual. */
  openActivateBatchFromSelection(): void {
    if (this.selectedActivateIds.size === 0 || !this.history) return;
    const ids = Array.from(this.selectedActivateIds);
    // Tomamos la versión del primer cuota encontrado para informar al operador.
    let versionRef: HistoryVersion | null = null;
    for (const v of this.history.versions) {
      if (v.cuotas?.some(c => this.selectedActivateIds.has(c.id))) { versionRef = v; break; }
    }
    this.activateModal = {
      open: true,
      mode: 'batch',
      cuota: null,
      versionRef,
      cuotaIds: ids,
      comment: ''
    };
  }

  closeActivateModal(): void {
    this.activateModal.open = false;
  }

  confirmActivate(): void {
    const comment = (this.activateModal.comment || '').trim();
    if (comment.length < 10) {
      this.toast.showToast('error', 'Motivo obligatorio (mín. 10 caracteres).');
      return;
    }
    const ids = this.activateModal.cuotaIds;
    if (!ids || ids.length === 0) {
      this.toast.showToast('error', 'No hay cuotas para activar.');
      return;
    }

    const isCorporate = this.enrollmentType === 'corporate';
    let req$;
    if (this.activateModal.mode === 'single') {
      const id = ids[0];
      req$ = isCorporate
        ? this.corpAdminService.activate(id, { comment })
        : this.adminService.activate(id, { comment });
    } else {
      const payload = { installmentIds: ids, comment };
      req$ = isCorporate
        ? this.corpAdminService.activateBatch(payload)
        : this.adminService.activateBatch(payload);
    }

    this.loadingService.show();
    req$.subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.toast.showToast('success', data?.message ?? 'Cuota(s) activadas.');
        this.closeActivateModal();
        this.clearActivateSelection();
        this.load();
      },
      error: err => {
        const msg = err?.error?.message ?? err?.error ?? 'Error al activar la(s) cuota(s).';
        this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
      },
      complete: () => this.loadingService.hide()
    });
  }

  /** Versiones a las que se puede mover la cuota actual (excluye la suya + scope corp). */
  get availableTargetVersions(): HistoryVersion[] {
    if (!this.moveModal.cuota || !this.history) return [];
    const sourceVersionId = this.moveModal.sourceVersion?.paymentPlanVersionId ?? null;
    return this.history.versions.filter(v => {
      // Solo versiones con id real (no legacy con paymentPlanVersionId=null)
      if (!v.paymentPlanVersionId) return false;
      // Excluir la versión actual de la cuota
      if (v.paymentPlanVersionId === sourceVersionId) return false;
      // Para corp: el scope (participantId) tiene que coincidir
      if (this.enrollmentType === 'corporate') {
        if (v.participantId !== this.moveModal.cuota!.participantId) return false;
      }
      return true;
    });
  }

  openMoveModal(cuota: HistoryCuota, sourceVersion: HistoryVersion): void {
    this.moveModal = {
      open: true,
      cuota,
      sourceVersion,
      targetVersionId: null,
      comment: '',
      preserveTotalAmount: false
    };
  }

  closeMoveModal(): void {
    this.moveModal.open = false;
  }

  /** 🆕 El operador acepta el cambio del modal de confirmación. Reintenta con flag.
   * Soporta tanto move single como batch (lee `_pendingConfirmIsBatch`). */
  acceptConfirmDialog(): void {
    this.confirmDialog.open = false;
    if (this._pendingConfirmIsBatch) {
      this._pendingConfirmIsBatch = false;
      this.confirmBatchMove(true);
    } else {
      this.confirmMove(true);
    }
  }

  /** 🆕 El operador cancela: cierra el modal de confirm pero deja el modal de move abierto. */
  rejectConfirmDialog(): void {
    this.confirmDialog.open = false;
  }

  /** 🆕 Helper de formato para el modal de confirmación. */
  formatConfirmDiff(): string {
    if (this.confirmDialog.currentValue == null || this.confirmDialog.proposedValue == null) return '';
    const diff = this.confirmDialog.proposedValue - this.confirmDialog.currentValue;
    return (diff >= 0 ? '+' : '') + this.formatCurrency(diff);
  }

  // ============================================================
  // 🆕 Mover MÚLTIPLES cuotas a otra versión (batch)
  // ============================================================

  /**
   * Clave única del grupo activo en modo multi-select. Null = off.
   * Formato: `v:{paymentPlanVersionId}` para versiones reales, `l:{version}`
   * para grupos virtuales (cuotas con PaymentPlanVersionId=null agrupadas por
   * la columna legacy `PaymentPlan.Version`).
   *
   * Antes era `number | null` comparando contra `v.paymentPlanVersionId`, pero
   * eso hacía que todos los grupos virtuales (ambos null) colapsaran a la
   * misma clave: la columna SEL aparecía "siempre" en virtuales sin haber
   * activado multi-select, y el toggle entre ellos era ambiguo.
   */
  multiSelectVersionId: string | null = null;

  /** Clave única para identificar un grupo del historial en multi-select. */
  groupKey(v: HistoryVersion): string {
    return v.paymentPlanVersionId != null
      ? `v:${v.paymentPlanVersionId}`
      : `l:${v.version}`;
  }

  /** Estado del modal "Mover varias a versión". */
  batchMoveModal: {
    open: boolean;
    sourceVersion: HistoryVersion | null;
    selectedCuotas: HistoryCuota[]; // ya ordenadas por _selectionOrder
    targetVersionId: number | null;
    comment: string;
    useSelectionOrderForMoved: boolean;
    /** 🆕 Si true, el backend no toca Enrollment.TotalAmount aunque la suma cambie. */
    preserveTotalAmount: boolean;
  } = { open: false, sourceVersion: null, selectedCuotas: [], targetVersionId: null, comment: '', useSelectionOrderForMoved: false, preserveTotalAmount: false };

  /** Versiones disponibles como destino para el batch (mismo criterio que single). */
  get availableBatchTargetVersions(): HistoryVersion[] {
    if (!this.batchMoveModal.sourceVersion || !this.history) return [];
    const sourceVersionId = this.batchMoveModal.sourceVersion.paymentPlanVersionId ?? null;
    // Scope corp se infiere del primer seleccionado.
    const firstSelected = this.batchMoveModal.selectedCuotas[0];
    return this.history.versions.filter(v => {
      if (!v.paymentPlanVersionId) return false;
      if (v.paymentPlanVersionId === sourceVersionId) return false;
      if (this.enrollmentType === 'corporate') {
        if (!firstSelected) return false;
        if (v.participantId !== firstSelected.participantId) return false;
      }
      return true;
    });
  }

  /** Cuotas seleccionadas en la versión actual de multi-select, ordenadas por _selectionOrder. */
  selectedCuotasInVersion(version: HistoryVersion): HistoryCuota[] {
    return version.cuotas
      .filter(c => c._selectionOrder != null)
      .sort((a, b) => (a._selectionOrder ?? 0) - (b._selectionOrder ?? 0));
  }

  /** ¿Hay selecciones activas en alguna versión? */
  get hasAnySelection(): boolean {
    return this.history?.versions.some(v => v.cuotas.some(c => c._selectionOrder != null)) ?? false;
  }

  /** Inicia o cancela el modo multi-select en una versión. */
  toggleMultiSelectMode(version: HistoryVersion): void {
    const key = this.groupKey(version);
    if (this.multiSelectVersionId === key) {
      // Salir del modo y limpiar selecciones.
      this.clearAllSelections();
      this.multiSelectVersionId = null;
    } else {
      // Limpiar selecciones previas en otras versiones (multi-select es por-versión).
      this.clearAllSelections();
      this.multiSelectVersionId = key;
    }
  }

  /** Toggle de selección de una cuota: añade al final o quita y compacta. */
  toggleCuotaSelection(cuota: HistoryCuota, version: HistoryVersion): void {
    // Solo se permite en la versión que esté en modo multi-select.
    if (this.multiSelectVersionId !== this.groupKey(version)) return;
    if (cuota._selectionOrder != null) {
      // Quitar selección y compactar órdenes restantes.
      const removed = cuota._selectionOrder;
      cuota._selectionOrder = null;
      version.cuotas
        .filter(c => c._selectionOrder != null && (c._selectionOrder as number) > removed)
        .forEach(c => { c._selectionOrder = (c._selectionOrder as number) - 1; });
    } else {
      // Agregar al final.
      const currentMax = Math.max(0, ...version.cuotas.map(c => c._selectionOrder ?? 0));
      cuota._selectionOrder = currentMax + 1;
    }
  }

  /** Limpia todas las selecciones (UI). */
  clearAllSelections(): void {
    this.history?.versions.forEach(v => v.cuotas.forEach(c => c._selectionOrder = null));
  }

  /** Abre el modal batch usando las selecciones de la versión actual. */
  openBatchMoveModal(sourceVersion: HistoryVersion): void {
    const selected = this.selectedCuotasInVersion(sourceVersion);
    if (selected.length === 0) {
      this.toast.showToast('warning', 'Seleccioná al menos una cuota antes de mover.');
      return;
    }
    this.batchMoveModal = {
      open: true,
      sourceVersion,
      selectedCuotas: selected,
      targetVersionId: null,
      comment: '',
      useSelectionOrderForMoved: false,
      preserveTotalAmount: false
    };
  }

  closeBatchMoveModal(): void {
    this.batchMoveModal.open = false;
  }

  /** El operador acepta el modal de confirm secundario tras el warning de total. */
  acceptBatchConfirmDialog(): void {
    this.confirmDialog.open = false;
    this.confirmBatchMove(true);
  }

  confirmBatchMove(confirmTotalChange: boolean = false): void {
    if (!this.batchMoveModal.selectedCuotas.length || !this.batchMoveModal.targetVersionId) {
      this.toast.showToast('error', 'Faltan datos: cuotas o versión destino.');
      return;
    }
    const comment = (this.batchMoveModal.comment || '').trim();
    if (comment.length < 10) {
      this.toast.showToast('error', 'Motivo obligatorio (mín. 10 caracteres).');
      return;
    }

    const payload: MoveBatchToVersionRequest = {
      installmentIds: this.batchMoveModal.selectedCuotas.map(c => c.id),
      targetVersionId: this.batchMoveModal.targetVersionId!,
      comment,
      useSelectionOrderForMoved: this.batchMoveModal.useSelectionOrderForMoved,
      confirmTotalChange,
      preserveTotalAmount: this.batchMoveModal.preserveTotalAmount
    };

    const svc = this.enrollmentType === 'corporate'
      ? this.corpAdminService.moveBatchToVersion(payload)
      : this.adminService.moveBatchToVersion(payload);

    this.loadingService.show();
    // 🔧 finalize() en vez de complete-only: garantiza hide() en error también.
    svc.pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          if (data?.requiresConfirmation === true) {
            this.confirmDialog = {
              open: true,
              title: 'Confirmar cambio del monto de inscripción',
              message: data.message ?? 'El movimiento batch requiere confirmación.',
              currentValue: typeof data.currentValue === 'number' ? data.currentValue : null,
              proposedValue: typeof data.proposedValue === 'number' ? data.proposedValue : null,
              confirmationKey: data.confirmationKey ?? null
            };
            // Flag para que el accept del confirmDialog dispare el batch.
            this._pendingConfirmIsBatch = true;
            return;
          }
          this.toast.showToast('success', data?.message ?? 'Movidas.');
          this.closeBatchMoveModal();
          this.multiSelectVersionId = null;
          this.clearAllSelections();
          this.load();
        },
        error: (err) => {
          const msg = err?.error?.message ?? err?.error ?? 'Error al mover las cuotas.';
          this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
        }
      });
  }

  /** Flag interno para que el confirm-dialog accept sepa si vino de single o batch. */
  _pendingConfirmIsBatch = false;

  // ============================================================
  // 🆕 Consolidar versión activa (one-click fix para casos legacy)
  // ============================================================
  consolidateModal: {
    open: boolean;
    comment: string;
  } = { open: false, comment: '' };

  /** ¿Es individual? La consolidación sólo aplica a inscripciones individuales por ahora. */
  get canConsolidate(): boolean {
    return this.isSuperAdmin && this.enrollmentType === 'individual';
  }

  openConsolidateModal(): void {
    this.consolidateModal = { open: true, comment: '' };
  }

  closeConsolidateModal(): void {
    this.consolidateModal.open = false;
  }

  // ============================================================
  // 🆕 Cambiar versión activa (sólo flip de PaymentPlanVersion.IsActive,
  //     sin mover cuotas ni renumerar)
  // ============================================================
  setActiveVersionModal: {
    open: boolean;
    targetVersionId: number | null;
    comment: string;
  } = { open: false, targetVersionId: null, comment: '' };

  /** Sólo aplica a individuales (corp no expone este flow por ahora). */
  get canSetActiveVersion(): boolean {
    return this.isSuperAdmin && this.enrollmentType === 'individual';
  }

  openSetActiveVersionModal(): void {
    // Pre-seleccionamos la versión que hoy está marcada como activa (si hay una sola)
    const currentlyActive = (this.history?.versions ?? []).filter(v => v.isActive && v.paymentPlanVersionId);
    const preselected = currentlyActive.length === 1 ? currentlyActive[0].paymentPlanVersionId! : null;
    this.setActiveVersionModal = {
      open: true,
      targetVersionId: preselected,
      comment: ''
    };
  }

  closeSetActiveVersionModal(): void {
    this.setActiveVersionModal.open = false;
  }

  /** Versiones candidatas (las que tienen paymentPlanVersionId real — no legacy). */
  get setActiveCandidateVersions(): HistoryVersion[] {
    return (this.history?.versions ?? []).filter(v => v.paymentPlanVersionId != null);
  }

  confirmSetActiveVersion(): void {
    const comment = (this.setActiveVersionModal.comment || '').trim();
    if (comment.length < 10) {
      this.toast.showToast('error', 'Motivo obligatorio (mín. 10 caracteres).');
      return;
    }
    if (this.setActiveVersionModal.targetVersionId == null) {
      this.toast.showToast('error', 'Elegí cuál versión queda como activa.');
      return;
    }
    const payload: SetActiveVersionRequest = {
      targetVersionId: this.setActiveVersionModal.targetVersionId,
      comment
    };
    this.loadingService.show();
    this.adminService.setActiveVersion(this.enrollmentId, payload)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          this.toast.showToast('success', data?.message ?? 'Versión activa actualizada.');
          this.closeSetActiveVersionModal();
          this.load();
        },
        error: (err) => {
          const msg = err?.error?.message ?? err?.error ?? 'Error al cambiar la versión activa.';
          this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
        }
      });
  }

  // ============================================================
  // 🆕 Promover grupo virtual a PaymentPlanVersion activa
  // ============================================================
  promoteGroupModal: {
    open: boolean;
    targetVersion: HistoryVersion | null;
    comment: string;
    sweepOtherOrphans: boolean;
  } = { open: false, targetVersion: null, comment: '', sweepOtherOrphans: true };

  /**
   * Un grupo es "promocionable" cuando es virtual (sin paymentPlanVersionId real),
   * tiene al menos una cuota activa y el usuario es super-admin. Aplica tanto a
   * inscripciones individuales (crea PaymentPlanVersion) como corporativas
   * (crea CorporatePaymentPlanVersion con Scope inferido).
   */
  canPromoteGroup(v: HistoryVersion): boolean {
    return this.isSuperAdmin
        && v.paymentPlanVersionId == null
        && v.cuotas.some(c => c.isActive);
  }

  /**
   * Para grupos virtuales corporativos: devuelve el ParticipantId compartido
   * por las cuotas del grupo (todas deben tener el mismo, sino devuelve null
   * que el backend rechazará). Null indica "grupo de empresa o ParticipantsAll".
   */
  getGroupParticipantId(v: HistoryVersion): number | null {
    const cuotasActivas = v.cuotas.filter(c => c.isActive);
    if (cuotasActivas.length === 0) return null;
    const first = cuotasActivas[0].participantId ?? null;
    return first;
  }

  /** Helper para template: label legible del ParticipantId del grupo. */
  getGroupParticipantIdLabel(v: HistoryVersion): string {
    const pid = this.getGroupParticipantId(v);
    return pid != null ? `#${pid}` : 'null (empresa/all)';
  }

  /** Etiqueta humana del scope inferido (sólo informativa en el modal corp). */
  inferredScopeLabel(v: HistoryVersion): string {
    if (this.enrollmentType !== 'corporate') return '';
    const pid = this.getGroupParticipantId(v);
    if (pid != null) {
      const name = v.cuotas.find(c => c.participantId === pid)?.participantName;
      return `SingleParticipant${name ? ` (${name})` : ` (id #${pid})`}`;
    }
    const cuotasActivas = v.cuotas.filter(c => c.isActive);
    const anyCompany = cuotasActivas.some(c => String(c.payerType).toUpperCase() === 'COMPANY' || c.payerType === 2);
    const anyNonCompany = cuotasActivas.some(c => !(String(c.payerType).toUpperCase() === 'COMPANY' || c.payerType === 2));
    if (anyCompany && anyNonCompany) return '⚠️ Ambiguo (mezcla COMPANY+PARTICIPANT) — el backend lo rechazará';
    if (anyCompany) return 'Company';
    return 'ParticipantsAll';
  }

  /** Cuántos otros grupos virtuales con cuotas activas existen (info p/ el modal). */
  otherOrphanGroupsCount(v: HistoryVersion): number {
    return (this.history?.versions ?? []).filter(g =>
      g !== v
      && g.paymentPlanVersionId == null
      && g.cuotas.some(c => c.isActive)
    ).length;
  }

  /** Cuántas cuotas activas hay en otros grupos huérfanos (sweep target). */
  otherOrphanActiveCuotasCount(v: HistoryVersion): number {
    return (this.history?.versions ?? [])
      .filter(g => g !== v && g.paymentPlanVersionId == null)
      .reduce((acc, g) => acc + g.cuotas.filter(c => c.isActive).length, 0);
  }

  openPromoteGroupModal(v: HistoryVersion): void {
    this.promoteGroupModal = {
      open: true,
      targetVersion: v,
      comment: '',
      sweepOtherOrphans: this.otherOrphanActiveCuotasCount(v) > 0
    };
  }

  closePromoteGroupModal(): void {
    this.promoteGroupModal.open = false;
  }

  confirmPromoteGroup(): void {
    const target = this.promoteGroupModal.targetVersion;
    const comment = (this.promoteGroupModal.comment || '').trim();
    if (!target) {
      this.toast.showToast('error', 'No se identificó el grupo a promover.');
      return;
    }
    if (comment.length < 10) {
      this.toast.showToast('error', 'Motivo obligatorio (mín. 10 caracteres).');
      return;
    }
    this.loadingService.show();

    const call$ = this.enrollmentType === 'corporate'
      ? this.corpAdminService.promoteGroupToVersion(this.enrollmentId, {
          legacyVersion: target.version,
          participantId: this.getGroupParticipantId(target),
          comment,
          sweepOtherOrphans: this.promoteGroupModal.sweepOtherOrphans
        } as PromoteGroupToCorporateVersionRequest)
      : this.adminService.promoteGroupToVersion(this.enrollmentId, {
          legacyVersion: target.version,
          comment,
          sweepOtherOrphans: this.promoteGroupModal.sweepOtherOrphans
        } as PromoteGroupToVersionRequest);

    call$
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          this.toast.showToast('success', data?.message ?? 'Grupo promovido a versión activa.');
          this.closePromoteGroupModal();
          this.load();
        },
        error: (err) => {
          const msg = err?.error?.message ?? err?.error ?? 'Error al promover el grupo.';
          this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
        }
      });
  }

  /** Ejecuta la consolidación: mueve TODAS las cuotas IsActive=true a la versión activa. */
  confirmConsolidate(): void {
    const comment = (this.consolidateModal.comment || '').trim();
    if (comment.length < 10) {
      this.toast.showToast('error', 'Motivo obligatorio (mín. 10 caracteres).');
      return;
    }
    const payload: ConsolidateActiveVersionRequest = { comment };
    this.loadingService.show();
    // 🔧 finalize() garantiza loadingService.hide() siempre — el handler `complete`
    // de RxJS no se llama tras `error`, así que en caso de error el spinner queda
    // colgado. Mismo bug que ya tuvimos en runScan().
    this.adminService.consolidateActiveVersion(this.enrollmentId, payload)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          this.toast.showToast('success', data?.message ?? 'Versión activa consolidada.');
          this.closeConsolidateModal();
          this.load();
        },
        error: (err) => {
          const msg = err?.error?.message ?? err?.error ?? 'Error al consolidar.';
          this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
        }
      });
  }

  confirmMove(confirmTotalChange: boolean = false): void {
    if (!this.moveModal.cuota || !this.moveModal.targetVersionId) {
      this.toast.showToast('error', 'Seleccioná la versión destino.');
      return;
    }
    const comment = (this.moveModal.comment || '').trim();
    if (comment.length < 10) {
      this.toast.showToast('error', 'Motivo obligatorio (mín. 10 caracteres).');
      return;
    }

    const installmentId = this.moveModal.cuota.id;
    const payload: any = {
      targetVersionId: this.moveModal.targetVersionId,
      comment,
      confirmTotalChange,
      preserveTotalAmount: this.moveModal.preserveTotalAmount
    };
    const svc = this.enrollmentType === 'corporate'
      ? this.corpAdminService.moveToVersion(installmentId, payload)
      : this.adminService.moveToVersion(installmentId, payload);

    this.loadingService.show();
    // 🔧 finalize() en vez de complete-only.
    svc.pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;

          // 🆕 Backend pide confirmación explícita (ej: alteraría TotalAmount)
          if (data?.requiresConfirmation === true) {
            this.confirmDialog = {
              open: true,
              title: data.confirmationKey === 'TOTAL_AMOUNT_CHANGE'
                ? 'Confirmar cambio del monto de inscripción'
                : 'Confirmar acción',
              message: data.message ?? 'El movimiento requiere confirmación.',
              currentValue: typeof data.currentValue === 'number' ? data.currentValue : null,
              proposedValue: typeof data.proposedValue === 'number' ? data.proposedValue : null,
              confirmationKey: data.confirmationKey ?? null
            };
            return;
          }

          this.toast.showToast('success', data?.message ?? 'Cuota movida.');
          this.closeMoveModal();
          this.load();
        },
        error: (err) => {
          const msg = err?.error?.message ?? err?.error ?? 'Error al mover la cuota.';
          this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
        }
      });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const typeParam = this.route.snapshot.queryParamMap.get('type');
    const participantParam = this.route.snapshot.queryParamMap.get('participantId');
    this.enrollmentId = Number(idParam) || 0;
    this.enrollmentType = typeParam === 'corporate' ? 'corporate' : 'individual';
    const pid = Number(participantParam);
    this.participantIdFilter = participantParam && pid > 0 ? pid : null;

    if (this.enrollmentId > 0) {
      this.load();
    } else {
      this.error = 'Id de inscripción inválido';
    }
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentPlansService.getHistory(this.enrollmentId, this.enrollmentType).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        const versions: HistoryVersion[] = (data?.versions ?? []).map((v: HistoryVersion, idx: number) => ({
          ...v,
          _expanded: idx === 0
        }));
        this.history = {
          enrollmentId: data?.enrollmentId ?? this.enrollmentId,
          enrollmentType: data?.enrollmentType ?? this.enrollmentType,
          versions
        };
        this.applyParticipantFilter();
        this.loading = false;
      },
      error: err => {
        this.error = err?.message ?? 'No se pudo cargar el historial';
        this.loading = false;
      }
    });
  }

  private applyParticipantFilter(): void {
    if (!this.history) {
      this.filteredVersions = [];
      return;
    }
    if (this.participantIdFilter == null) {
      this.filteredVersions = this.history.versions;
      this.participantNameFilter = null;
      return;
    }
    const pid = this.participantIdFilter;
    this.filteredVersions = this.history.versions.filter(v => {
      const scope = (v.scope ?? '').toLowerCase();
      // Versiones globales (afectan a todos los participantes): mantener
      if (scope === 'company' || scope === 'participantsall') return true;
      // Versiones de scope SingleParticipant: sólo las de este participante
      if (scope === 'singleparticipant') return v.participantId === pid;
      // Sin scope (individual o legacy) — mantener si tiene cuotas del participante
      return v.cuotas?.some(c => c.participantId === pid) ?? true;
    });
    // Tomar nombre del participante del primer match disponible para mostrar en el header
    const namedVersion = this.history.versions.find(v => v.participantId === pid && !!v.participantName);
    this.participantNameFilter = namedVersion?.participantName
      ?? this.history.versions.flatMap(v => v.cuotas ?? []).find(c => c.participantId === pid)?.participantName
      ?? null;
  }

  toggleVersion(version: HistoryVersion): void {
    version._expanded = !version._expanded;
  }

  toggleCuota(cuota: HistoryCuota): void {
    cuota._expanded = !cuota._expanded;
  }

  hasPaymentData(cuota: HistoryCuota): boolean {
    return (cuota.incomeReceipts?.length ?? 0) > 0
      || !!cuota.paymentLink
      || (cuota.wompiWebhooks?.length ?? 0) > 0;
  }

  receiptStatusLabel(receipt: HistoryReceipt): string {
    if (receipt.isRefunded) return 'Reembolsado';
    if (receipt.isCancelled) return 'Anulado';
    if (!receipt.isActive) return 'Inactivo';
    if (receipt.approvalStatus === 'PendingApproval') return 'Pendiente aprobación';
    return 'Activo';
  }

  receiptStatusClass(receipt: HistoryReceipt): string {
    if (receipt.isRefunded) return 'bg-purple-100 text-purple-700';
    if (receipt.isCancelled) return 'bg-red-100 text-red-700';
    if (!receipt.isActive) return 'bg-amber-100 text-amber-700';
    if (receipt.approvalStatus === 'PendingApproval') return 'bg-yellow-100 text-yellow-700';
    return 'bg-emerald-100 text-emerald-700';
  }

  webhookStatusClass(status?: string): string {
    switch ((status ?? '').toUpperCase()) {
      case 'APPROVED': return 'bg-emerald-100 text-emerald-700';
      case 'DECLINED':
      case 'ERROR': return 'bg-red-100 text-red-700';
      case 'PENDING': return 'bg-yellow-100 text-yellow-700';
      case 'VOIDED': return 'bg-gray-100 text-gray-700';
      default: return 'bg-cyan-100 text-cyan-700';
    }
  }

  formatWebhookAmount(amountInCents: number, currency?: string): string {
    const amount = (amountInCents ?? 0) / 100;
    const code = currency || 'COP';
    try {
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(amount);
    } catch {
      return `${amount} ${code}`;
    }
  }

  goBack(): void {
    if (this.enrollmentType === 'corporate') {
      this.router.navigate(['/dashboard/enrollments/corporate/view-v2', this.enrollmentId]);
    } else {
      this.router.navigate(['/dashboard/enrollments/view-v2', this.enrollmentId]);
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value ?? 0);
  }

  reasonLabel(reason: number | null | undefined): string {
    if (reason == null) return '';
    return PaymentPlanVersionReasonLabels[reason as PaymentPlanVersionReason] ?? '';
  }

  paymentStatusLabel(status: string | number): string {
    const map: Record<string, string> = {
      '0': 'Pendiente',
      '1': 'Pagado',
      '2': 'Link generado',
      '3': 'Vencido',
      'PENDING': 'Pendiente',
      'PAID': 'Pagado',
      'LINK_GENERATED': 'Link generado',
      'OVERDUE': 'Vencido'
    };
    const key = String(status);
    return map[key] ?? key;
  }

  movementTypeLabel(mt: string | number): string {
    const map: Record<string, string> = {
      '0': 'Ninguno',
      '1': 'Retiro',
      '2': 'Aplazamiento',
      '3': 'Reversión',
      'None': 'Ninguno',
      'Withdrawal': 'Retiro',
      'Deferral': 'Aplazamiento',
      'Reversal': 'Reversión'
    };
    const key = String(mt);
    return map[key] ?? '';
  }
}
